import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { getBranding, type PublicBranding } from "../lib/branding.functions";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { CookieBanner } from "../components/festival/CookieBanner";
import { AnalyticsLoader } from "../components/festival/AnalyticsLoader";
import { BackToTop } from "../components/festival/BackToTop";
import { BrandingProvider } from "../components/festival/BrandingProvider";
import { PageMediaProvider } from "../components/festival/PageMediaProvider";
import { TicketsProvider } from "../components/festival/TicketsProvider";
import { Starfield } from "../components/festival/Starfield";
import heroMobile from "../assets/uploads/hero-mobile.webp.asset.json";
import heroDesktop from "../assets/uploads/hero-desktop.webp.asset.json";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async ({ context }): Promise<{ branding: PublicBranding }> => {
    // Fetch branding server-side so the correct logo is in the first paint
    // (prevents a flash of the bundled default logo before hydration).
    const branding = await context.queryClient.ensureQueryData<PublicBranding>({
      queryKey: ["branding"],
      queryFn: () => getBranding(),
      staleTime: 5 * 60 * 1000,
    });
    return { branding };
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Lovable" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Openluchtfilmfestival Apeldoorn" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/tQwYDxTVISW1actQg5tKg5hunXM2/social-images/social-1784196621063-Schermafbeelding_2026-07-16_121004.webp" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/tQwYDxTVISW1actQg5tKg5hunXM2/social-images/social-1784196621063-Schermafbeelding_2026-07-16_121004.webp" },
      { name: "google-site-verification", content: "TWeA4lm98BcQmFe35g8UkcJDYSj-dlBN1T0s9P6yDPI" },
      { title: "Openlucht Filmfestival Apeldoorn | Films onder de sterren" },
      { property: "og:title", content: "Openlucht Filmfestival Apeldoorn | Films onder de sterren" },
      { name: "twitter:title", content: "Openlucht Filmfestival Apeldoorn | Films onder de sterren" },
      { name: "description", content: "Beleef van 14 t/m 22 augustus 2026 topfilms onder de sterren in Stadspark Berg & Bos in Apeldoorn. Tickets, programma en praktische info." },
      { property: "og:description", content: "Beleef van 14 t/m 22 augustus 2026 topfilms onder de sterren in Stadspark Berg & Bos in Apeldoorn. Tickets, programma en praktische info." },
      { name: "twitter:description", content: "Beleef van 14 t/m 22 augustus 2026 topfilms onder de sterren in Stadspark Berg & Bos in Apeldoorn. Tickets, programma en praktische info." },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon.png" },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "48x48" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        as: "image",
        href: heroMobile.url,
        type: "image/webp",
        media: "(max-width: 767px)",
        fetchPriority: "high",
      } as any,
      {
        rel: "preload",
        as: "image",
        href: heroDesktop.url,
        type: "image/webp",
        media: "(min-width: 768px)",
        fetchPriority: "high",
      } as any,
    ],
    scripts: [
      {
        children:
          "(function(){var l=document.createElement('link');l.rel='stylesheet';l.href='https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400&display=swap';l.media='print';l.onload=function(){l.media='all'};document.head.appendChild(l);})();",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname }) ?? "/";
  const lang = pathname.startsWith("/en") ? "en" : pathname.startsWith("/de") ? "de" : "nl";
  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        <script dangerouslySetInnerHTML={{ __html: "try{history.scrollRestoration='manual'}catch(e){}" }} />
        {/* Critical above-the-fold CSS — paints the hero before styles.css finishes parsing */}
        <style
          dangerouslySetInnerHTML={{
            __html:
              "html,body{margin:0;background:oklch(0.11 0.035 250);color:oklch(0.95 0.015 80);font-family:'Inter',ui-sans-serif,system-ui,sans-serif;-webkit-font-smoothing:antialiased}h1,h2,h3,h4,.font-display{font-family:'Fraunces',ui-serif,Georgia,serif;font-synthesis-weight:none}#top{position:relative;min-height:100svh;overflow:hidden;isolation:isolate}#top>picture>img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:-20}",
          }}
        />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { branding } = Route.useLoaderData();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const hash = useRouterState({ select: (s) => s.location.hash });

  // Scroll to top on every route change and on hard refresh, unless a hash
  // anchor is present (e.g. /programma#film-3). TanStack's built-in
  // scrollRestoration is disabled because it restores stale positions on
  // hard refreshes where async content (film data, images) shifts page height.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (hash) return;
    window.scrollTo(0, 0);
  }, [pathname, hash]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrandingProvider initialBranding={branding}>
        <PageMediaProvider>
          <TicketsProvider>
            <Starfield />
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
            <BackToTop />
            <CookieBanner />
            <AnalyticsLoader />
          </TicketsProvider>
        </PageMediaProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
}
